import { useEffect, useState } from "react";
import { api } from "@/lib/api-client";
import styles from "./automation-guide.module.css";

/**
 * F-065 — the read-only automation guide.
 *
 * A procedural reference for operators: what the system checks and what it does
 * to a document, in plain language. Deliberately inert — no authoring
 * affordance, no configuration, not even a filter control — because the whole
 * point of the tenant setting behind it is that reading is the entire
 * permission being granted. The server (`GET /api/automations/guide`) returns
 * prose only; there is no config here to leak.
 */

interface GuideAutomation {
  id: string;
  name: string;
  description: string;
  summary: string;
}

interface GuideJobType {
  id: string;
  name: string;
  automations: GuideAutomation[];
}

interface Guide {
  jobTypes: GuideJobType[];
}

export function AutomationGuidePage() {
  const [guide, setGuide] = useState<Guide | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<Guide>("/automations/guide").then(
      (data) => {
        if (!cancelled) setGuide(data);
      },
      () => {
        if (!cancelled) {
          setError(
            "The automation guide is not available for your account. Ask an administrator to turn it on in Tenant Settings.",
          );
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const populated = guide?.jobTypes.filter((jt) => jt.automations.length > 0) ?? [];

  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>Automation Guide</h1>
      <p className={styles.lede}>
        What the system checks and does on its own, for each job type. Read-only —
        automations are authored by administrators.
      </p>

      {error ? (
        <p className={styles.empty} role="status">{error}</p>
      ) : !guide ? (
        <p className={styles.empty}>Loading…</p>
      ) : populated.length === 0 ? (
        <p className={styles.empty}>No published automations yet.</p>
      ) : (
        populated.map((jobType) => (
          <section key={jobType.id} className={styles.jobType}>
            <h2 className={styles.jobTypeName}>{jobType.name}</h2>
            <ul className={styles.list}>
              {jobType.automations.map((automation) => (
                <li key={automation.id} className={styles.row}>
                  <span className={styles.name}>{automation.name}</span>
                  <span className={styles.summary}>{automation.summary}</span>
                  {automation.description && (
                    <span className={styles.description}>{automation.description}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
